import { type FC } from "react";
import { Link } from "react-router-dom";

import { Text } from "@mantine/core";

import { useRemoteAssetsRoutes } from "../../embed/routes/useRemoteAssetsRoutes";
import { DOMAIN } from "../../shared/constants";
import { useRemoteAssetLensUsages } from "../hooks/useRemoteAssetLensUsages";

import { AndMoreLenses, LensUsagesError, LoadingLensUsages } from "./messages.generated";

interface RemoteAssetLensUsageExpandContentProps {
    assetId: string;
    lensUsages: number;
    orgId: string;
    containerClassName: string;
    linkClassName: string;
    moreClassName: string;
    textSize: "xs" | "sm";
}

export const RemoteAssetLensUsageExpandContent: FC<RemoteAssetLensUsageExpandContentProps> = ({
    assetId,
    lensUsages,
    orgId,
    containerClassName,
    linkClassName,
    moreClassName,
    textSize,
}) => {
    const routes = useRemoteAssetsRoutes();
    const { lenses, loading, error } = useRemoteAssetLensUsages(assetId);

    return (
        <div className={containerClassName}>
            {loading && lenses.length === 0 && (
                <Text size={textSize} c="dimmed">
                    <LoadingLensUsages />
                </Text>
            )}
            {error && lenses.length === 0 && (
                <Text size={textSize} c="red">
                    <LensUsagesError />
                </Text>
            )}
            {lenses.map((lens) => {
                const lensPath = routes.lensDetail(lens.lensId);
                if (lensPath) {
                    return (
                        <Link
                            key={lens.lensId}
                            to={lensPath}
                            className={linkClassName}
                            onClick={(event) => {
                                event.stopPropagation();
                            }}
                        >
                            {lens.lensName}
                        </Link>
                    );
                }
                return (
                    <button
                        key={lens.lensId}
                        type="button"
                        className={linkClassName}
                        onClick={(event) => {
                            event.stopPropagation();
                            routes.openExternal(`https://${DOMAIN}/lens/${lens.lensId}`);
                        }}
                    >
                        {lens.lensName}
                    </button>
                );
            })}
            {!loading && lenses.length > 0 && lensUsages > lenses.length && (
                <Link
                    to={routes.assetDetail(orgId, assetId)}
                    className={moreClassName}
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <AndMoreLenses count={lensUsages - lenses.length} />
                </Link>
            )}
        </div>
    );
};
